/**
 * Hook to register a JSX instruction block with InstructionContext.
 *
 * Renders the JSX tree to a single string via the instruction reconciler
 * and registers it as one block. Priority and placement are block-level
 * (not per-element) to guarantee stable ordering.
 *
 * @example
 * ```tsx
 * function MyInstructions() {
 *   useRegisterInstructions(
 *     <>
 *       <tag-identity>You are Atlas</tag-identity>
 *       <p>Be helpful.</p>
 *     </>,
 *     { priority: 10 },
 *   );
 *   return null;
 * }
 * ```
 */

import type { ReactNode } from 'react';
import { use, useEffect, useEffectEvent } from 'react';

import { renderInstructions } from './reconciler.ts';
import type { Placement, RenderedBlock } from './types.ts';
import { InstructionContext } from '#plugin-chat/contexts/InstructionContext.ts';

/**
 * Registers a JSX instruction block with InstructionContext.
 *
 * The JSX tree is rendered to a single string in document order (no per-tag
 * priority). Priority and placement are block-level properties passed via
 * `options` — all content inside the block shares them.
 *
 * @param element - JSX content (any mix of text, lists, `<tag-*>`, etc.)
 * @param options - `priority` (default 50) and `placement` (default 'system')
 */
export function useRegisterInstructions(
  element: ReactNode,
  options: { priority?: number; placement?: Placement } = {},
): void {
  const { priority = 50, placement = 'system' } = options;
  const { registerInstruction } = use(InstructionContext);
  const registerInstructionEvent = useEffectEvent(registerInstruction);

  const content = renderInstructions(element);

  useEffect(() => {
    if (!content) {
      return;
    }
    const block: RenderedBlock = { priority, placement, content };
    return registerInstructionEvent(block);
  }, [content, priority, placement]);
}
